<template>
  <re-dialog :title="$t('global.helpcenter.contact_form.title')">
    <re-form
      ref="formComponent"
      class="column full-width"
      @submit="sendFeedback"
    >
      <p>
        {{ $t('global.helpcenter.contact_form.content') }}
      </p>

      <re-select
        v-model="contactForm.module"
        name="module"
        :label="$t('common.module')"
        :options="[
          ...accessibleModules.map((m) => ({
            label: $t(`value.${m}`),
            value: m,
          })),
          {
            label: $t(`monolith.shared.na`),
            value: '-',
          },
        ]"
        map-options
        emit-value
      />

      <re-select
        v-model="contactForm.subject"
        name="subject_tech"
        map-options
        emit-value
        :label="$t('global.helpcenter.contact_form.subject_label')"
        :options="
          subjects.map((subject) => ({
            value: subject,
            label: $t(`global.helpcenter.contact_form.subject_${subject}`),
          }))
        "
      />

      <re-input
        v-if="contactForm.subject === 'other'"
        v-model="contactForm.otherSubject"
        :label="$t('global.helpcenter.contact_form.subject_other')"
      />

      <re-select
        v-model="contactForm.valuation_request_ref"
        name="valuation_request_ref"
        map-options
        emit-value
        :label="$t('valuer_tool.general.title')"
        :options="requestRefOptions"
        class="fit"
      >
        <template #option="{ opt, itemProps }">
          <!-- Divider for "-------" -->
          <div
            v-if="opt?.disable"
            class="q-px-md q-py-sm text-grey-8"
          >
            {{ opt.label }}
          </div>

          <!-- All selectable options -->
          <q-item
            v-else
            v-bind="itemProps"
            class="q-py-md"
          >
            <q-item-section
              v-if="!opt?.value"
              class="full-width"
            >
              <q-item-label>{{ opt.label }}</q-item-label>
            </q-item-section>

            <template v-else>
              <q-item-section top>
                <template v-if="opt?.customerRef">
                  <q-item-label caption>
                    {{ $t('monolith.shared.customer_ref') }}{{ $t('punctuation.:') }}&nbsp;
                  </q-item-label>

                  <q-item-label lines="1">
                    {{ opt?.customerRef }}
                  </q-item-label>
                </template>

                <template v-else>
                  <q-item-label caption>
                    {{ $t('monolith.shared.request_ref') }}{{ $t('punctuation.:') }}&nbsp;
                  </q-item-label>

                  <q-item-label lines="1">
                    {{ opt?.value || opt.label }}
                  </q-item-label>
                </template>
              </q-item-section>

              <q-item-section top>
                <q-item-label
                  v-if="opt?.address"
                  caption
                >
                  {{ $t('monolith.shared.address') }}{{ $t('punctuation.:') }}&nbsp;
                </q-item-label>

                <q-item-label lines="4">
                  {{ opt?.address }}
                </q-item-label>
              </q-item-section>

              <q-item-section
                v-if="opt?.valuationType"
                top
                avatar
                side
                class="text-center"
              >
                <q-avatar
                  round
                  size="sm"
                >
                  <re-icon :name="`ic-${opt.valuationType}`" />
                </q-avatar>

                <q-item-label class="text-caption text-weight-bold">
                  {{ opt?.valuationType.toUpperCase() }}
                </q-item-label>
              </q-item-section>
            </template>
          </q-item>
        </template>

        <template #after-options>
          <div class="q-ma-md">
            <re-button
              stretch
              :disabled="isLoading"
              size="xs"
              @click="loadMoreDays()"
            >
              {{ t('dashboard.table.load_more') }}
              <q-spinner
                v-if="isLoading"
                class="q-ml-md"
              />

              <q-icon
                v-else
                class="q-ml-md"
                name="mdi-arrow-down"
              />
            </re-button>
          </div>
        </template>
      </re-select>

      <re-input
        v-model="contactForm.content"
        :label="$t('global.helpcenter.contact_form.message')"
        type="textarea"
        class="full-width"
        required
        name="content"
      />

      <FileUploader
        class="re-mt"
        :accept="['image/png', 'image/jpeg']"
        @upload="(file) => contactForm.screenshots.push(...file)"
      />

      <p class="text-caption">
        {{ $t('global.helpcenter.contact_form.screenshot_info') }}
      </p>

      <template
        v-for="(screenshot, i) in contactForm.screenshots"
        :key="i"
      >
        <DocumentSection
          is-legacy
          class="re-mt"
          :file="screenshot"
          @delete="deleteScreenshot"
        />
      </template>
    </re-form>

    <template #actions>
      <re-button
        v-close-popup="3"
        type="submit"
        color="main"
        padding="sm xl"
        size="md"
        class="self-end"
        :label="$t('global.helpcenter.contact_form.submit')"
        @click="sendFeedback"
      />
    </template>
  </re-dialog>
</template>

<script setup>
import { useQuasar } from 'quasar'
import { computed, defineAsyncComponent, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { queryKeyToUrl, ReApi, ReApiVersion, ReEndpoint } from '@/composables/query'
import { useCurUser } from '@/composables/useCurUser'
import { useRequestList } from '@/composables/useRequestList'
import axios from '@/plugins/axios'
import enums from '@/plugins/enums'
import utils from '@/plugins/utils'

import FileUploader from '../../components/FileUploader.vue'

const props = defineProps({
  module: {
    type: String,
    default: null,
  },
  subject: {
    type: String,
    default: null,
  },
  valuationRequestRef: {
    type: String,
    default: null,
  },
})

// Lazy import: DocumentSection statically imports the valuation v3 API client,
// which builds an axios instance from store config at module-eval time. Loading
// it eagerly via the App.vue → ContactForm chain runs before SET_CONFIG and
// crashes the app (config.VALUATION_API_URL on null). Defer it past config load.
const DocumentSection = defineAsyncComponent(
  () => import('@/apps/ValuerTool/components/DocMgmt/DocumentSection.vue'),
)

const { accessibleModules } = useCurUser()
const $q = useQuasar()
const { t } = useI18n()

const formComponent = ref()

const defaultContactFormData = {
  module: '',
  subject: '',
  valuation_request_ref: null,
  otherSubject: '',
  content: null,
  screenshots: [],
}

const contactForm = reactive({ ...defaultContactFormData })

watch(
  () => [props.module, props.subject, props.valuationRequestRef],
  ([newModule, newSubject, newValuationRequestRef]) => {
    contactForm.module = newModule
    contactForm.subject = newSubject
    contactForm.valuation_request_ref = newValuationRequestRef
  },
  { immediate: true },
)

const limit = ref(50)
const { data: userRequests, isLoading } = useRequestList({ limit, isUserRequests: true })

function loadMoreDays() {
  limit.value += 50
}

const requestRefOptions = computed(() => {
  const options = userRequests.value.map((r) => {
    const addressText = utils.display_address(r)
    return {
      label: `${r.valuation_request_ref} • ${addressText ? `${addressText} • ` : ''}${r.valuation_type?.toUpperCase() ?? ''}`,
      value: r.valuation_request_ref,
      customerRef: r.customer_ref,
      address: addressText,
      valuationType: r.valuation_type,
    }
  })

  return [
    { value: '', label: t('global.helpcenter.contact_form.not_specific_req') },
    { label: '-------', disable: true, value: '-------' },
    ...options,
  ]
})
const subjects = computed(() => {
  const selectedModule = contactForm.module

  if (selectedModule === enums.modules.ERS) {
    return [
      enums.contactFormSubjects.REQ_FAILING,
      enums.contactFormSubjects.CANT_DOWNLOAD_VAL_REPORT,
      enums.contactFormSubjects.REQ_NOT_IN_DASHBOARD,
      enums.contactFormSubjects.CANCEL_REQ,
      enums.contactFormSubjects.OTHER,
    ]
  }

  if (
    selectedModule === enums.modules.DVM
    || selectedModule === enums.modules.NONE
    || selectedModule === ''
  ) {
    return [
      enums.contactFormSubjects.REQ_NOT_EVAL_YET,
      enums.contactFormSubjects.REQ_BACK_TO_DRAFT,
      enums.contactFormSubjects.CANT_DOWNLOAD_VAL_REPORT,
      enums.contactFormSubjects.REQ_NOT_IN_DASHBOARD,
      enums.contactFormSubjects.CANCEL_REQ,
      enums.contactFormSubjects.REQ_CUSTOM_QUOTE,
      enums.contactFormSubjects.OTHER,
    ]
  }

  if (selectedModule === enums.modules.OEPC) {
    return [enums.contactFormSubjects.REQ_CUSTOM_QUOTE, enums.contactFormSubjects.OTHER]
  }

  if (selectedModule === enums.modules.OVM) {
    return [
      enums.contactFormSubjects.REQ_CUSTOM_QUOTE,
      enums.contactFormSubjects.REQ_APPOINTMENT,
      enums.contactFormSubjects.OTHER,
    ]
  }

  return []
})

const route = useRoute()

onMounted(() => {
  if (route.query.from_module) {
    contactForm.module = route.query.from_module
  }
  if (route.query.valuation_request_ref) {
    contactForm.valuation_request_ref = route.query.valuation_request_ref
  }
})

let subscriptionsOnSend = new Set()
function subscribeOnSend(cb) {
  subscriptionsOnSend.add(cb)
  return () => subscriptionsOnSend.delete(cb)
}

defineExpose({
  setOptions(opts) {
    Object.assign(contactForm, opts)
  },
  subscribeOnSend,
})

function resetContactForm() {
  Object.assign(contactForm, {
    ...defaultContactFormData,
    module: props.module,
    subject: props.subject,
    valuation_request_ref: props.valuationRequestRef,
  })

  subscriptionsOnSend = new Set()
}

function deleteScreenshot(file) {
  return contactForm.screenshots = contactForm.screenshots.filter(screenshot => screenshot !== file)
}

async function sendFeedback(event) {
  if (!(await formComponent.value.validate(true))) {
    event.stopPropagation()

    return
  }

  const { otherSubject, screenshots, subject, module, content, valuation_request_ref } = contactForm
  // TODO : Replace below commented API call to the proper endpoint and
  // remove the lines above this comment block and below the commented
  // API call

  const formData = new FormData()
  if (subject === 'other') {
    formData.append('subject', otherSubject)
  }
  else {
    formData.append('subject', t(`global.helpcenter.contact_form.subject_${subject}`))
  }
  formData.append('module', module)
  formData.append('content', content)
  formData.append('to', 'support')

  let queryKey = [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.CONTACT]
  if (valuation_request_ref) {
    queryKey = [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST, valuation_request_ref, 'contact']
    formData.append('valuation_request_ref', valuation_request_ref)
  }

  formData.append(
    'url',
    JSON.stringify({
      params: route.params,
      fullPath: route.fullPath,
      query: route.query,
      hash: route.hash,
      name: route.name,
    }),
  )
  for (let i = 0; i < screenshots.length; i++) {
    formData.append(`screenshot_${i}`, screenshots[i])
  }

  const data = {}
  formData.forEach((value, key) => {
    data[key] = value
  })

  const notifyOnSend = (status = 'success') => {
    for (const cb of subscriptionsOnSend.values()) {
      cb({
        data,
        status,
      })
    }
  }

  notifyOnSend('init')
  axios
    .post(queryKeyToUrl(queryKey).url, formData, {
      headers: { 'content-type': 'multipart/form-data' },
    })
    .then(() => {
      notifyOnSend()
      $q.notify({
        type: enums.feedbackType.POSITIVE,
        message: `${t('global.helpcenter.contact_form.feedback_title')}, ${t('global.helpcenter.contact_form.feedback_content')}`,
      })
      resetContactForm()
    })
    .catch((e) => {
      notifyOnSend('fail')
      throw new Error(e)
    })
}
</script>
